import React, {useEffect, useReducer, useRef, useContext, useState, useCallback} from "react";
import {Button, Alert, Row} from "antd";
import axios from "@common/axios";
import MathJaxHTMLContent from "@common/MathJaxHTMLContent";
import CircularLoader from "@common/CircularLoader";
import {CheckCircleOutlined, CloseCircleOutlined} from "@ant-design/icons";
import TrailFeedback from "../questions/TrailFeedback";
import {TrailsContext} from "../trails/student/StudentTrail";
import Question, {QuestionAnswerResultBlock} from "../questions/Question";
import {ContentContext} from "@context/ContentContext";

const ACTIONS = {
    FETCH_START: 'FETCH_START',
    FETCH_SUCCESS: 'FETCH_SUCCESS',
    FETCH_ERROR: 'FETCH_ERROR',
    SUBMIT_START: 'SUBMIT_START',
    SUBMIT_SUCCESS: 'SUBMIT_SUCCESS',
    SUBMIT_ERROR: 'SUBMIT_ERROR',
    UPDATE_SUBMITTABLE: 'UPDATE_SUBMITTABLE',
    TRY_AGAIN: 'TRY_AGAIN',
    NEXT_STEP: 'NEXT_STEP',
    UPDATE_DND_RESULTS_VIEW: 'UPDATE_DND_RESULTS_VIEW',
};
const walkthroughStepInitialState = {
    isLoading: true,
    isSubmitting: false,
    error: null,
    stepQuestion: null,
    stepResult: null,
    attemptCount: 1,
    submittable: false,
    dndResultView: null,
};

function walkthroughStepReducer(state, action) {
    switch (action.type) {
        case ACTIONS.FETCH_START:
            return {...walkthroughStepInitialState, isLoading: true};
        case ACTIONS.FETCH_SUCCESS:
            return {
                ...state,
                isLoading: false,
                stepQuestion: action.payload.stepQuestion
            };
        case ACTIONS.FETCH_ERROR:
            return {...state, isLoading: false, error: action.payload};
        case ACTIONS.SUBMIT_START:
            return {...state, isSubmitting: true, error: null};
        case ACTIONS.SUBMIT_SUCCESS:
            return {
                ...state,
                isSubmitting: false,
                stepResult: action.payload,
                attemptCount: action.payload.attemptCount ?? state.attemptCount + 1
            };
        case ACTIONS.SUBMIT_ERROR:
            return {...state, isSubmitting: false, error: action.payload};
        case ACTIONS.UPDATE_SUBMITTABLE:
            return {...state, submittable: action.payload};
        case ACTIONS.TRY_AGAIN:
            return {...state, stepResult: null, error: null};
        case ACTIONS.NEXT_STEP:
            return {
                ...state,
                stepResult: null,
                error: null,
                submittable: false,
                attemptCount: walkthroughStepInitialState.attemptCount
            };
        case ACTIONS.UPDATE_DND_RESULTS_VIEW:
            return {...state, dndResultView: action.payload};
        default:
            return state;
    }
}

const WalkthroughStepCore = props => {
    const {
        questionId,
        conceptId,
        trailStepId,
        stepNo,
        stepTotal,
        loadStepUrl,
        answerStepUrl,
        onAnswer,
        onNextStep,
        setShowSummary,
        WalkthroughCompleteBtn
    } = props;
    const [state, dispatch] = useReducer(walkthroughStepReducer, walkthroughStepInitialState);
    const answerRef = useRef("");

    useEffect(() => {
        let isActive = true;
        const loadStep = async () => {
            dispatch({type: ACTIONS.FETCH_START});
            try {
                const response = await axios.get(loadStepUrl, {
                    params: {
                        conceptId,
                        questionId,
                        ...(trailStepId && {stepId: trailStepId}),
                        stepNo
                    }
                });
                if (isActive) dispatch({type: ACTIONS.FETCH_SUCCESS, payload: response.data});
            } catch (error) {
                if (isActive) dispatch({type: ACTIONS.FETCH_ERROR, payload: 'Failed to load step'});
            }
        };
        loadStep();

        return () => {isActive = false};
    }, [questionId, conceptId, trailStepId, stepNo, loadStepUrl]);

    const handleUpdateQuestionData = useCallback((questionAnswers, isSubmittable) => {
        answerRef.current = questionAnswers;
        dispatch({type: ACTIONS.UPDATE_SUBMITTABLE, payload: isSubmittable});
    }, []);

    const handleTryAgain = () => {
        dispatch({type: ACTIONS.TRY_AGAIN});
    };

    const handleNextStep = () => {
        dispatch({type: ACTIONS.NEXT_STEP});
        onNextStep?.();
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        dispatch({type: ACTIONS.SUBMIT_START});

        try {
            const answerBody = answerRef.current;
            const response = await axios.post(
                answerStepUrl,
                answerBody,
                {
                    params: {
                        questionId,
                        conceptId,
                        stepQuestionId: state.stepQuestion.questionId,
                        attemptCount: state.attemptCount,
                        ...(trailStepId && {stepId: trailStepId})
                    },
                    headers: { "Content-Type": "application/json" }
                }
            );
            dispatch({type: ACTIONS.SUBMIT_SUCCESS, payload: response.data});
            onAnswer?.(response.data);
        } catch (error) {
            console.error('Error submitting walkthrough answer:', error);
            dispatch({type: ACTIONS.SUBMIT_ERROR, payload: error.response?.data?.message || 'Error submitting'});
        }
    };

    return (
        <WalkthroughStepBody
            title={`Step ${stepNo} of ${stepTotal}`}
        >
            {state.isLoading && (
                <Row>
                    <CircularLoader displayInElement={true} />
                </Row>
            )}
            {state.error && (
                <Alert type="error" title="Step is invalid" role="alert" />
            )}
            {state?.stepQuestion && (
                <>
                    <form
                        className="ch-q-form"
                        onSubmit={handleSubmit}
                    >
                        <Question
                            question={state.stepQuestion}
                            isLoading={state.stepQuestion === null}
                            updateQuestionData={handleUpdateQuestionData}
                            {...state.stepResult && {
                                result: state.stepResult,
                                dndResultView: state.dndResultView,
                            }}
                            displayAnswerOnResult={false}
                            resultFormat="json"
                        />
                        {!state?.stepResult && (
                            <Row className="margin-t--m mediumGap">
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    disabled={!state.submittable || state.isSubmitting}
                                    aria-label="Submit walkthrough answer"
                                >
                                    Answer
                                </Button>
                            </Row>
                        )}
                    </form>
                    {state?.stepResult && (
                        <TrailFeedback
                            title={state.stepResult.result ? (
                                <>
                                    <CheckCircleOutlined className="ch-correct-icon" aria-hidden="true" />
                                    <strong>Correct</strong>
                                </>
                            ) : (
                                <>
                                    <CloseCircleOutlined className="ch-incorrect-icon" aria-hidden="true" />
                                    <strong>Incorrect</strong>
                                </>
                            )}
                            buttons={!state.stepResult.result && state.attemptCount <= 2 ? (
                                <Button
                                    onClick={handleTryAgain}
                                    aria-label="Try the question again"
                                    type="primary"
                                >
                                    Try Again
                                </Button>
                            ) : stepNo === stepTotal ? (
                                <>
                                    <Button
                                        type="default"
                                        aria-label="View summary of walkthrough"
                                        onClick={() => setShowSummary(true)}
                                    >
                                        Summary
                                    </Button>
                                    {WalkthroughCompleteBtn && (
                                        <WalkthroughCompleteBtn />
                                    )}
                                </>
                            ) : (
                                <Button
                                    onClick={handleNextStep}
                                    aria-label="Go to next step"
                                    type="primary"
                                >
                                    Next Step
                                </Button>
                            )}
                        >
                            <QuestionAnswerResultBlock
                                result={state.stepResult}
                                questionAnswerType={state.currentQuestion?.questionAnswerType}
                                dndValue={state.dndResultView}
                                dndOnChange={view => dispatch({type: ACTIONS.UPDATE_DND_RESULTS_VIEW, payload: view})}
                                isWalkthrough={true}
                                inDialog={true}
                            />
                        </TrailFeedback>
                    )}
                </>
            )}
        </WalkthroughStepBody>
    );
};

const WalkthroughStep = props => {
    const {...other} = props;
    return (
        <WalkthroughStepCore
            {...other}
            loadStepUrl="/web/ui/walkthrough/step"
            answerStepUrl="/web/ui/walkthrough/step/answer"
        />
    )
}

const TrailWalkthroughStep = props => {
    const {onAnswer, setWalkthroughCompleteBtnLabel, ...other} = props;
    const trailsContext = useContext(TrailsContext);
    return (
        <WalkthroughStepCore
            {...other}
            loadStepUrl="/web/ui/walkthrough/trail/step"
            answerStepUrl="/web/ui/walkthrough/trail/step/answer"
            onAnswer={(responseData) => {
                if (responseData.trailStepComplete) trailsContext?.updateContinue?.(true);
                setWalkthroughCompleteBtnLabel(responseData.trailStepComplete === false ? "Next Walkthrough" : "Continue");
                onAnswer?.(responseData);
            }}
        />
    )
}

const WalkthroughSummary = props => {
    const {question, completed = false, WalkthroughCompleteBtn} = props;
    const [showSummaryComments, setShowSummaryComments] = useState(false);
    const commentsBtnRef = useRef();
    const {content} = useContext(ContentContext);

    useEffect(() => {
        if (commentsBtnRef.current) {
            commentsBtnRef.current.focus({focusVisible: false, preventScroll: true});
        }
    }, []);

    return (
        <WalkthroughStepBody title="Summary">
            <MathJaxHTMLContent html={question.summarySolution} showLoader={false} className={content?.currentSyllabus?.syllabusContentClass}/>
            {showSummaryComments && (
                <TrailFeedback
                    title={<>Comments</>}
                >
                    <MathJaxHTMLContent html={question.summary} showLoader={false} className={content?.currentSyllabus?.syllabusContentClass}/>
                </TrailFeedback>
            )}
            <Row justify="end" className="margin-t--m mediumGap">
                {!showSummaryComments && (
                    <Button ref={commentsBtnRef} onClick={() => setShowSummaryComments(true)}>
                        Comments
                    </Button>
                )}
                {completed && WalkthroughCompleteBtn && (
                    <WalkthroughCompleteBtn />
                )}
            </Row>
        </WalkthroughStepBody>
    )
}

const WalkthroughStepBody = props => {
    const {title, children} = props;

    return (
        <section aria-labelledby="walkthrough_step_title" className="margin-t--l">
            {title && (
                <h4 id="walkthrough_step_title">
                    {title}
                </h4>
            )}
            <div className="margin-t--m">
                {children}
            </div>
        </section>
    )
}

export {WalkthroughStep, TrailWalkthroughStep, WalkthroughSummary};